/**
 * OutlineIcon Component
 *
 * Handles rendering of Hero icons with outline style.
 *
 * For component documentation, icon management, and optimization guidelines,
 * please refer to the README.md file in this directory.
 */
import {
  AdjustmentsHorizontalIcon,
  ArrowDownIcon,
  ArrowDownTrayIcon,
  ArrowLeftEndOnRectangleIcon,
  ArrowLeftIcon,
  ArrowLongRightIcon,
  ArrowPathIcon,
  ArrowRightIcon,
  ArrowRightStartOnRectangleIcon,
  ArrowsPointingInIcon,
  ArrowsPointingOutIcon,
  ArrowsRightLeftIcon,
  ArrowsUpDownIcon,
  ArrowTopRightOnSquareIcon,
  ArrowTrendingDownIcon,
  ArrowTrendingUpIcon,
  ArrowTurnDownRightIcon,
  ArrowUpIcon,
  ArrowUpTrayIcon,
  ArrowUturnLeftIcon,
  BanknotesIcon,
  Bars2Icon,
  Bars3Icon,
  Bars4Icon,
  BeakerIcon,
  BellIcon,
  BoltIcon,
  BookOpenIcon,
  BriefcaseIcon,
  BuildingLibraryIcon,
  BuildingOffice2Icon,
  BuildingOfficeIcon,
  CalculatorIcon,
  CalendarDaysIcon,
  CalendarIcon,
  ChartBarIcon,
  ChartBarSquareIcon,
  ChatBubbleBottomCenterTextIcon,
  CheckBadgeIcon,
  CheckCircleIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronUpDownIcon,
  ChevronUpIcon,
  CircleStackIcon,
  ClockIcon,
  CloudArrowDownIcon,
  Cog6ToothIcon,
  CogIcon,
  ComputerDesktopIcon,
  CubeIcon,
  CubeTransparentIcon,
  CurrencyDollarIcon,
  DocumentDuplicateIcon,
  DocumentMagnifyingGlassIcon,
  DocumentPlusIcon,
  EllipsisHorizontalIcon,
  EllipsisVerticalIcon,
  EqualsIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  EyeSlashIcon,
  ForwardIcon,
  FunnelIcon,
  GlobeAltIcon,
  HandRaisedIcon,
  HomeIcon,
  HomeModernIcon,
  IdentificationIcon,
  InboxArrowDownIcon,
  InboxIcon,
  InformationCircleIcon,
  ListBulletIcon,
  LockClosedIcon,
  LockOpenIcon,
  MagnifyingGlassIcon,
  MapPinIcon,
  MicrophoneIcon,
  MinusCircleIcon,
  MinusIcon,
  MoonIcon,
  NoSymbolIcon,
  PaperAirplaneIcon,
  PaperClipIcon,
  PauseCircleIcon,
  PencilIcon,
  PencilSquareIcon,
  PlayCircleIcon,
  PlusIcon,
  PresentationChartLineIcon,
  QuestionMarkCircleIcon,
  ReceiptPercentIcon,
  RectangleGroupIcon,
  RectangleStackIcon,
  RssIcon,
  ShareIcon,
  ShieldCheckIcon,
  ShieldExclamationIcon,
  ShoppingCartIcon,
  SignalIcon,
  SignalSlashIcon,
  SparklesIcon,
  Square2StackIcon,
  Square3Stack3DIcon,
  Squares2X2Icon,
  SquaresPlusIcon,
  StopCircleIcon,
  SunIcon,
  SwatchIcon,
  TableCellsIcon,
  TrashIcon,
  UserCircleIcon,
  UserGroupIcon,
  UserIcon,
  ViewfinderCircleIcon,
  WrenchScrewdriverIcon,
  XCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'

import { IconBase, type IconBaseProps } from './base'
import { IconName as HeroIconName } from './types'

const outlineIconMap: Partial<
  Record<HeroIconName, React.ComponentType<React.SVGProps<SVGSVGElement>>>
> = {
  [HeroIconName.AdjustmentsHorizontalIcon]: AdjustmentsHorizontalIcon,
  [HeroIconName.ArrowUpIcon]: ArrowUpIcon,
  [HeroIconName.ArrowDownIcon]: ArrowDownIcon,
  [HeroIconName.ArrowDownTrayIcon]: ArrowDownTrayIcon,
  [HeroIconName.ArrowLeftEndOnRectangleIcon]: ArrowLeftEndOnRectangleIcon,
  [HeroIconName.ArrowLeftIcon]: ArrowLeftIcon,
  [HeroIconName.ArrowLongRightIcon]: ArrowLongRightIcon,
  [HeroIconName.ArrowPathIcon]: ArrowPathIcon,
  [HeroIconName.ArrowRightIcon]: ArrowRightIcon,
  [HeroIconName.ArrowTopRightOnSquareIcon]: ArrowTopRightOnSquareIcon,
  [HeroIconName.ArrowTrendingDownIcon]: ArrowTrendingDownIcon,
  [HeroIconName.ArrowTrendingUpIcon]: ArrowTrendingUpIcon,
  [HeroIconName.ArrowTurnDownRightIcon]: ArrowTurnDownRightIcon,
  [HeroIconName.ArrowRightStartOnRectangleIcon]: ArrowRightStartOnRectangleIcon,
  [HeroIconName.ArrowUpTrayIcon]: ArrowUpTrayIcon,
  [HeroIconName.ArrowUturnLeftIcon]: ArrowUturnLeftIcon,
  [HeroIconName.ArrowsPointingInIcon]: ArrowsPointingInIcon,
  [HeroIconName.ArrowsPointingOutIcon]: ArrowsPointingOutIcon,
  [HeroIconName.ArrowsRightLeftIcon]: ArrowsRightLeftIcon,
  [HeroIconName.ArrowsUpDownIcon]: ArrowsUpDownIcon,
  [HeroIconName.BanknotesIcon]: BanknotesIcon,
  [HeroIconName.Bars2Icon]: Bars2Icon,
  [HeroIconName.Bars3Icon]: Bars3Icon,
  [HeroIconName.Bars4Icon]: Bars4Icon,
  [HeroIconName.BeakerIcon]: BeakerIcon,
  [HeroIconName.BellIcon]: BellIcon,
  [HeroIconName.HomeIcon]: HomeIcon,
  [HeroIconName.BoltIcon]: BoltIcon,
  [HeroIconName.BookOpenIcon]: BookOpenIcon,
  [HeroIconName.BriefcaseIcon]: BriefcaseIcon,
  [HeroIconName.BuildingLibraryIcon]: BuildingLibraryIcon,
  [HeroIconName.BuildingOffice2Icon]: BuildingOffice2Icon,
  [HeroIconName.BuildingOfficeIcon]: BuildingOfficeIcon,
  [HeroIconName.CalculatorIcon]: CalculatorIcon,
  [HeroIconName.CalendarDaysIcon]: CalendarDaysIcon,
  [HeroIconName.CalendarIcon]: CalendarIcon,
  [HeroIconName.ChartBarIcon]: ChartBarIcon,
  [HeroIconName.ChartBarSquareIcon]: ChartBarSquareIcon,
  [HeroIconName.ChatBubbleBottomCenterTextIcon]: ChatBubbleBottomCenterTextIcon,
  [HeroIconName.CheckBadgeIcon]: CheckBadgeIcon,
  [HeroIconName.CheckCircleIcon]: CheckCircleIcon,
  [HeroIconName.CheckIcon]: CheckIcon,
  [HeroIconName.ChevronDownIcon]: ChevronDownIcon,
  [HeroIconName.ChevronLeftIcon]: ChevronLeftIcon,
  [HeroIconName.ChevronRightIcon]: ChevronRightIcon,
  [HeroIconName.ChevronUpDownIcon]: ChevronUpDownIcon,
  [HeroIconName.ChevronUpIcon]: ChevronUpIcon,
  [HeroIconName.CircleStackIcon]: CircleStackIcon,
  [HeroIconName.ClockIcon]: ClockIcon,
  [HeroIconName.CloudArrowDownIcon]: CloudArrowDownIcon,
  [HeroIconName.CogIcon]: CogIcon,
  [HeroIconName.Cog6ToothIcon]: Cog6ToothIcon,
  [HeroIconName.ComputerDesktopIcon]: ComputerDesktopIcon,
  [HeroIconName.CubeIcon]: CubeIcon,
  [HeroIconName.CubeTransparentIcon]: CubeTransparentIcon,
  [HeroIconName.CurrencyDollarIcon]: CurrencyDollarIcon,
  [HeroIconName.DocumentDuplicateIcon]: DocumentDuplicateIcon,
  [HeroIconName.DocumentMagnifyingGlassIcon]: DocumentMagnifyingGlassIcon,
  [HeroIconName.DocumentPlusIcon]: DocumentPlusIcon,
  [HeroIconName.EllipsisHorizontalIcon]: EllipsisHorizontalIcon,
  [HeroIconName.EllipsisVerticalIcon]: EllipsisVerticalIcon,
  [HeroIconName.EqualsIcon]: EqualsIcon,
  [HeroIconName.ExclamationCircleIcon]: ExclamationCircleIcon,
  [HeroIconName.ExclamationTriangleIcon]: ExclamationTriangleIcon,
  [HeroIconName.EyeIcon]: EyeIcon,
  [HeroIconName.EyeSlashIcon]: EyeSlashIcon,
  [HeroIconName.ForwardIcon]: ForwardIcon,
  [HeroIconName.FunnelIcon]: FunnelIcon,
  [HeroIconName.GlobeAltIcon]: GlobeAltIcon,
  [HeroIconName.HandRaisedIcon]: HandRaisedIcon,
  [HeroIconName.HomeModernIcon]: HomeModernIcon,
  [HeroIconName.LockClosedIcon]: LockClosedIcon,
  [HeroIconName.LockOpenIcon]: LockOpenIcon,
  [HeroIconName.ListBulletIcon]: ListBulletIcon,
  [HeroIconName.IdentificationIcon]: IdentificationIcon,
  [HeroIconName.InboxArrowDownIcon]: InboxArrowDownIcon,
  [HeroIconName.InboxIcon]: InboxIcon,
  [HeroIconName.InformationCircleIcon]: InformationCircleIcon,
  [HeroIconName.QuestionMarkCircleIcon]: QuestionMarkCircleIcon,
  [HeroIconName.MapPinIcon]: MapPinIcon,
  [HeroIconName.MagnifyingGlassIcon]: MagnifyingGlassIcon,
  [HeroIconName.MicrophoneIcon]: MicrophoneIcon,
  [HeroIconName.MinusCircleIcon]: MinusCircleIcon,
  [HeroIconName.MinusIcon]: MinusIcon,
  [HeroIconName.MoonIcon]: MoonIcon,
  [HeroIconName.NoSymbolIcon]: NoSymbolIcon,
  [HeroIconName.PaperAirplaneIcon]: PaperAirplaneIcon,
  [HeroIconName.PaperClipIcon]: PaperClipIcon,
  [HeroIconName.PencilIcon]: PencilIcon,
  [HeroIconName.PencilSquareIcon]: PencilSquareIcon,
  [HeroIconName.PlayCircleIcon]: PlayCircleIcon,
  [HeroIconName.PauseCircleIcon]: PauseCircleIcon,
  [HeroIconName.PlusIcon]: PlusIcon,
  [HeroIconName.PresentationChartLineIcon]: PresentationChartLineIcon,
  [HeroIconName.RectangleGroupIcon]: RectangleGroupIcon,
  [HeroIconName.RectangleStackIcon]: RectangleStackIcon,
  [HeroIconName.ReceiptPercentIcon]: ReceiptPercentIcon,
  [HeroIconName.RssIcon]: RssIcon,
  [HeroIconName.ShareIcon]: ShareIcon,
  [HeroIconName.ShieldCheckIcon]: ShieldCheckIcon,
  [HeroIconName.ShieldExclamationIcon]: ShieldExclamationIcon,
  [HeroIconName.ShoppingCartIcon]: ShoppingCartIcon,
  [HeroIconName.SignalIcon]: SignalIcon,
  [HeroIconName.SignalSlashIcon]: SignalSlashIcon,
  [HeroIconName.SparklesIcon]: SparklesIcon,
  [HeroIconName.Square2StackIcon]: Square2StackIcon,
  [HeroIconName.Square3Stack3DIcon]: Square3Stack3DIcon,
  [HeroIconName.Squares2X2Icon]: Squares2X2Icon,
  [HeroIconName.SquaresPlusIcon]: SquaresPlusIcon,
  [HeroIconName.StopCircleIcon]: StopCircleIcon,
  [HeroIconName.SunIcon]: SunIcon,
  [HeroIconName.SwatchIcon]: SwatchIcon,
  [HeroIconName.TableCellsIcon]: TableCellsIcon,
  [HeroIconName.TrashIcon]: TrashIcon,
  [HeroIconName.UserCircleIcon]: UserCircleIcon,
  [HeroIconName.UserGroupIcon]: UserGroupIcon,
  [HeroIconName.UserIcon]: UserIcon,
  [HeroIconName.ViewfinderCircleIcon]: ViewfinderCircleIcon,
  [HeroIconName.WrenchScrewdriverIcon]: WrenchScrewdriverIcon,
  [HeroIconName.XCircleIcon]: XCircleIcon,
  [HeroIconName.XMarkIcon]: XMarkIcon,
}

export function OutlineIcon({ iconName, ...props }: IconBaseProps) {
  const IconComponent = outlineIconMap[iconName as HeroIconName]

  if (!IconComponent) {
    throw new Error(`Outline icon "${iconName}" not found`)
  }

  return (
    <IconBase
      IconComponent={IconComponent}
      iconName={iconName}
      {...props}
      type="outline"
    />
  )
}
